import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';
import { Ref } from 'vue-demi';

import { HITL } from '@/api';
import { OneReachApp, AppLauncherSettings } from '../../../types';
import { useAppsIntegrationAPI } from './use-apps-integration-api';
import { SETTINGS_CATEGORIES } from '@/constants';

export const useAppsIntegrationStore = defineStore('HITL-Apps-Integration', {
  state: () => {
    return {
      app: {} as OneReachApp,
      qrCodeUrl: '',
      error: null as object | null,
      isInitializing: false,
      isUpdating: false,
      isActivating: false,
    };
  },
  getters: {
    isAppsIntegrationReady: state => !_.isEmpty(state.app),
  },
});

export interface UseAppsIntegrationProps {
  settings: Ref<AppLauncherSettings>;
  getToken: () => string;
}

export const useAppsIntegrationActions = (options: UseAppsIntegrationProps) => {
  const store = useAppsIntegrationStore();
  const { settings, getToken } = options;
  const {
    getOneReachApp,
    getOneReachAppQRCodeURL,
    getOneReachAppQRCode,
    createOrUpdateOneReachApp,
  } = useAppsIntegrationAPI();

  /** App ID is stored in "app-launcher" settings if it was configured */
  const initializeAppsIntegration = async () => {
    if (store.isAppsIntegrationReady) {
      return;
    }

    try {
      store.isInitializing = true;
      const appId = settings.value.appId!;
      const token = getToken();

      const [app, qrCodeUrl] = await Promise.all([
        getOneReachApp({ appId, token }),
        getOneReachAppQRCode({ appId, token }),
      ]);

      store.app = app;
      store.qrCodeUrl = qrCodeUrl;
      store.isInitializing = false;
    } catch (e: any) {
      store.isInitializing = false;
      console.error('Error while initializing Apps Integration!', e);
    }
  };

  const openQRCodeURL = () => {
    const url = getOneReachAppQRCodeURL({ appId: store.app.id });
    window.open(url, '_blank');
  };

  const createOneReachApp = async (args: { app: OneReachApp; }) => {
    try {
      /** We have to provide default color when creating an app */
      const DEFAULT_COLOR = '42C3F8';
      store.isUpdating = true;

      const url = new URL(window.location.toString()).origin;
      const color = args.app.color || DEFAULT_COLOR;
      const actions = [{ type: 'web' as const, url }];

      const app = await createOrUpdateOneReachApp({
        app: { ...args.app, color, isActive: true, actions },
        type: 'create',
        token: getToken(),
      });

      const update = { ...settings.value, appId: app.id };
      await HITL.settings.updateSettings({
        isCommon: true,
        data: { [SETTINGS_CATEGORIES.LAUNCHER]: update },
      });

      const qrCodeUrl = await getOneReachAppQRCode({
        appId: app.id,
        token: getToken(),
      });

      store.app = app;
      store.qrCodeUrl = qrCodeUrl;
      store.isUpdating = false;
    } catch (e: any) {
      store.error = e;
      store.isUpdating = false;
      console.error('Error while creating Apps Integration!', e);
    }
  };

  const updateOneReachApp = async (args: { app: OneReachApp; }) => {
    try {
      /* Some properties are returned but not accepted by API */
      const SKIP_PROPERTIES = ['url', 'shareUrl', 'shareShortUrl', 'dateCreated', 'dateUpdated'];
      const property = args.app.isActive === store.app.isActive
        ? 'isUpdating'
        : 'isActivating';
      store[property] = true;

      const app = await createOrUpdateOneReachApp({
        app: _.omit(args.app, SKIP_PROPERTIES) as OneReachApp,
        type: 'update',
        token: getToken(),
      });

      store.app = app;
      store[property] = false;
    } catch (e: any) {
      store.error = e;
      store.isUpdating = false;
      store.isActivating = false;
      console.error('Error while updating Apps Integration!', e);
    }
  };

  return {
    initializeAppsIntegration,
    openQRCodeURL,
    createOneReachApp,
    updateOneReachApp,
  };
};

export const useAppsIntegration = (options: UseAppsIntegrationProps) => {
  return {
    ...storeToRefs(useAppsIntegrationStore()),
    ...useAppsIntegrationActions(options),
  };
};
